import { BillingSummaryProvider } from '../billing/BillingSummaryProvider';
import { AlertsProvider } from '../alerts/AlertsProvider';
import { DialogContent } from '../../components/ui/DialogContent';
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Alert, Box, Button, Modal, Paper, Snackbar } from '@mui/material';
import { DialogHeader } from '../../components/ui/DialogHeader';
import { DialogActions } from '../../components/ui/DialogActions';
import { usePhoneLayout } from '../../hooks/usePhoneLayout';
import { dialogSurface } from '../../components/ui/dialogSurface';

type Severity = 'success' | 'info' | 'warning' | 'error';

export interface ConfirmationNavigation {
  open: () => void;
  close: () => Promise<void>;
}

interface FeedbackApi {
  notify: (
    message: string,
    severity?: Severity,
    action?: { label: string; onClick: () => void },
  ) => void;
  confirm: (message: string, title?: string) => Promise<boolean>;
  confirmDiscardChanges: () => Promise<boolean>;
  cancelConfirmation: (options?: { fromNavigation?: boolean }) => void;
  hasNavigableConfirmation: () => boolean;
  registerConfirmationNavigation: (
    bridge: ConfirmationNavigation,
  ) => () => void;
  hasConfirmation: () => boolean;
}

interface ConfirmationState {
  message: string;
  title: string;
  navigable: boolean;
  confirmLabel: string;
  cancelLabel: string;
  confirmColor: 'primary' | 'error';
  safeAutofocus: boolean;
  resolve: (value: boolean) => void;
}

const FeedbackContext = createContext<FeedbackApi | null>(null);

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
  return (
    <AlertsProvider>
      <BillingSummaryProvider>
        <FeedbackContents>{children}</FeedbackContents>
      </BillingSummaryProvider>
    </AlertsProvider>
  );
}

function FeedbackContents({ children }: { children: React.ReactNode }) {
  const phone = usePhoneLayout();
  const [notification, setNotification] = useState<{
    message: string;
    severity: Severity;
    action?: { label: string; onClick: () => void };
  } | null>(null);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<ConfirmationState | null>(
    null,
  );
  const confirmationRef = useRef<ConfirmationState | null>(null);
  const settlingConfirmation = useRef(false);
  const confirmationNavigation = useRef<ConfirmationNavigation | null>(null);
  const notify = useCallback(
    (
      message: string,
      severity: Severity = 'info',
      action?: { label: string; onClick: () => void },
    ) => {
      setNotification({ message, severity, action });
      setNotificationOpen(true);
    },
    [],
  );
  const requestConfirmation = useCallback(
    (request: Omit<ConfirmationState, 'resolve'>) =>
      new Promise<boolean>((resolve) => {
        if (confirmationRef.current || settlingConfirmation.current) {
          resolve(false);
          return;
        }
        const next = { ...request, resolve };
        confirmationRef.current = next;
        setConfirmation(next);
        if (next.navigable) confirmationNavigation.current?.open();
      }),
    [],
  );
  const confirm = useCallback(
    (message: string, title = 'Please confirm') =>
      requestConfirmation({
        message,
        title,
        confirmLabel: 'Confirm',
        cancelLabel: 'Cancel',
        confirmColor: 'error',
        safeAutofocus: false,
        navigable: true,
      }),
    [requestConfirmation],
  );
  const confirmDiscardChanges = useCallback(
    () =>
      requestConfirmation({
        message: 'Your changes have not been saved.',
        title: 'Discard unsaved changes?',
        confirmLabel: 'Discard changes',
        cancelLabel: 'Keep editing',
        confirmColor: 'error',
        safeAutofocus: true,
        navigable: false,
      }),
    [requestConfirmation],
  );
  const settle = useCallback((accepted: boolean, fromNavigation = false) => {
    const current = confirmationRef.current;
    if (!current) return;
    confirmationRef.current = null;
    setConfirmation(null);
    if (
      current.navigable &&
      !fromNavigation &&
      confirmationNavigation.current
    ) {
      settlingConfirmation.current = true;
      void confirmationNavigation.current.close().then(() => {
        settlingConfirmation.current = false;
        current.resolve(accepted);
      });
    } else current.resolve(accepted);
  }, []);
  useEffect(() => {
    if (!confirmation) return undefined;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      settle(false);
    };
    window.addEventListener('keydown', closeOnEscape, true);
    return () => window.removeEventListener('keydown', closeOnEscape, true);
  }, [confirmation, settle]);
  const hasConfirmation = useCallback(
    () => confirmationRef.current !== null,
    [],
  );
  const hasNavigableConfirmation = useCallback(
    () => confirmationRef.current?.navigable === true,
    [],
  );
  const registerConfirmationNavigation = useCallback(
    (bridge: ConfirmationNavigation) => {
      confirmationNavigation.current = bridge;
      if (confirmationRef.current?.navigable) bridge.open();
      return () => {
        if (confirmationNavigation.current === bridge)
          confirmationNavigation.current = null;
      };
    },
    [],
  );
  const cancelConfirmation = useCallback(
    (options?: { fromNavigation?: boolean }) =>
      settle(false, options?.fromNavigation),
    [settle],
  );
  useEffect(
    () => () => {
      confirmationRef.current?.resolve(false);
    },
    [],
  );
  const value = useMemo(
    () => ({
      notify,
      confirm,
      confirmDiscardChanges,
      cancelConfirmation,
      hasConfirmation,
      hasNavigableConfirmation,
      registerConfirmationNavigation,
    }),
    [
      notify,
      confirm,
      confirmDiscardChanges,
      cancelConfirmation,
      hasConfirmation,
      hasNavigableConfirmation,
      registerConfirmationNavigation,
    ],
  );

  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <Snackbar
        open={notificationOpen}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          top: {
            xs: 'max(8px, env(safe-area-inset-top))',
            sm: 'max(24px, env(safe-area-inset-top))',
          },
        }}
        autoHideDuration={6000}
        onClose={() => setNotificationOpen(false)}
        slotProps={{
          transition: {
            onExited: () => {
              if (!notificationOpen) setNotification(null);
            },
          },
        }}
      >
        <Alert
          severity={notification?.severity || 'info'}
          variant="filled"
          onClose={() => setNotificationOpen(false)}
          action={
            notification?.action ? (
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  notification.action?.onClick();
                  setNotificationOpen(false);
                }}
              >
                {notification.action.label}
              </Button>
            ) : undefined
          }
          sx={{ whiteSpace: 'pre-line', width: '100%' }}
        >
          {notification?.message}
        </Alert>
      </Snackbar>
      {confirmation && (
        // Modal registers with MUI's modal stack, so an underlying Dialog stops
        // enforcing focus instead of fighting this confirmation's focus trap.
        <Modal
          open
          hideBackdrop
          disableEscapeKeyDown
          sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
        >
          <Box
            role="presentation"
            tabIndex={-1}
            onMouseDown={() => settle(false)}
            sx={{
              outline: 'none',
              position: 'fixed',
              inset: 0,
              zIndex: (theme) => theme.zIndex.modal + 1,
              bgcolor: 'rgba(34, 40, 30, 0.4)',
              display: 'flex',
              alignItems: phone ? 'flex-end' : 'center',
              justifyContent: 'center',
              p: phone ? 0 : 2,
            }}
          >
            <Paper
              role="dialog"
              aria-modal="true"
              aria-labelledby="feedback-confirmation-title"
              aria-describedby="feedback-confirmation-description"
              tabIndex={-1}
              onMouseDown={(event) => event.stopPropagation()}
              sx={{
                ...dialogSurface(phone),
                width: '100%',
                maxWidth: phone ? 'none' : 600,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
              }}
            >
              <DialogHeader
                id="feedback-confirmation-title"
                title={confirmation.title}
                onClose={() => settle(false)}
              />
              <DialogContent
                id="feedback-confirmation-description"
                sx={{ whiteSpace: 'pre-line' }}
              >
                {confirmation.message}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => settle(false)}
                  autoFocus={confirmation.safeAutofocus}
                >
                  {confirmation.cancelLabel}
                </Button>
                <Button
                  color={confirmation.confirmColor}
                  variant="contained"
                  onClick={() => settle(true)}
                  autoFocus={!confirmation.safeAutofocus}
                >
                  {confirmation.confirmLabel}
                </Button>
              </DialogActions>
            </Paper>
          </Box>
        </Modal>
      )}
    </FeedbackContext.Provider>
  );
}

export const useFeedback = (): FeedbackApi => {
  const context = useContext(FeedbackContext);
  if (!context)
    throw new Error('useFeedback must be used inside FeedbackProvider.');
  return context;
};
